import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '../../components/SearchableSelect';
import { countries } from '../../data/countries';
import { languages } from '../../data/languages';
import { orgNumberTerm } from '../../data/orgNumber';
import { supportedLanguages } from '../../i18n';
import { defaultLanguageForCountry } from '../../utils/locale';
import { AddressAutocomplete } from '../AddressAutocomplete';
import type { OnboardingDraft, OnboardingStepProps } from '../types';

// Step 1: organization name + address. The address can ONLY come from the
// Google Places-backed autocomplete (proxied through /api/v1/geocode) or its
// "find me" reverse-geocode — picking a result fans country, city, postal
// code, street, a suggested language, AND the geocoded coordinates onto the
// draft. There's no free-text address entry, so every club resolves to a real
// place and lands on the discovery map. A hand-typed fallback appears only if
// the geocoder is unavailable (503, e.g. no API key in dev); that path carries
// no coordinates and therefore no map pin.

export function OrgInfo({ draft, update }: OnboardingStepProps) {
  const { t, i18n } = useTranslation('common');

  const countryOptions = countries.map((c) => ({ value: c.code, label: c.name }));
  const languageOptions = languages.map((l) => ({
    value: l.code,
    label: l.nativeName,
    hint: l.englishName !== l.nativeName ? l.englishName : '',
  }));

  // Set language on the draft AND flip the SPA UI to match. Reused by the
  // language dropdown and by the address picker — picking a Swedish
  // address should also flip the page to Swedish, otherwise the form
  // reads Swedish in the data and English in the chrome and that's
  // jarring.
  const setLanguage = (code: string) => {
    update({ languageCode: code });
    const hasUiLocale = (supportedLanguages as readonly string[]).includes(code);
    if (hasUiLocale && i18n.language !== code) {
      void i18n.changeLanguage(code);
    }
  };

  return (
    <div className="form">
      <div className="form__field">
        <label htmlFor="org-name">{t('onboarding.orgInfo.name.label')}</label>
        <input
          id="org-name"
          type="text"
          value={draft.organizationName}
          onChange={(e) => update({ organizationName: e.target.value })}
          required
          maxLength={200}
          autoFocus
          autoComplete="organization"
        />
        <p className="form__hint">{t('onboarding.orgInfo.name.hint')}</p>
      </div>

      {!draft.manualEntry ? (
        <AutocompleteMode
          draft={draft}
          update={update}
          countryOptions={countryOptions}
          languageOptions={languageOptions}
          setLanguage={setLanguage}
        />
      ) : (
        <ManualMode
          draft={draft}
          update={update}
          countryOptions={countryOptions}
          languageOptions={languageOptions}
          setLanguage={setLanguage}
        />
      )}
    </div>
  );
}

interface SubMode {
  draft: OnboardingStepProps['draft'];
  update: OnboardingStepProps['update'];
  countryOptions: { value: string; label: string }[];
  languageOptions: { value: string; label: string; hint: string }[];
  setLanguage: (code: string) => void;
}

function AutocompleteMode({ draft, update, countryOptions, languageOptions, setLanguage }: SubMode) {
  const { t } = useTranslation('common');

  return (
    <>
      <AddressAutocomplete
        initialText={draft.addressSearchText}
        regionBias={draft.countryCode}
        language={draft.languageCode}
        onPicked={(place) => {
          update({
            addressSearchText: place.formatted_address,
            city: place.city,
            countryCode: place.country_code,
            officeStreet: place.street,
            officePostalCode: place.postal_code,
            officeCity: place.city,
            officeLatitude: place.latitude,
            officeLongitude: place.longitude,
          });
          setLanguage(defaultLanguageForCountry(place.country_code));
        }}
        onLocated={(place) => {
          // "Find me" reverse-geocodes the browser coordinates to a real
          // place — store everything it resolved (incl. the pin) so the club
          // lands on the map. No flip to manual entry: the address is always
          // Google-sourced.
          update({
            addressSearchText: place.formatted_address,
            officeLatitude: place.latitude,
            officeLongitude: place.longitude,
            ...(place.city ? { city: place.city, officeCity: place.city } : {}),
            ...(place.country_code ? { countryCode: place.country_code } : {}),
            ...(place.street ? { officeStreet: place.street } : {}),
            ...(place.postal_code ? { officePostalCode: place.postal_code } : {}),
          });
          if (place.country_code) {
            setLanguage(defaultLanguageForCountry(place.country_code));
          }
        }}
        onUnavailable={() => update({ manualEntry: true })}
        onClear={() => {
          update({
            addressSearchText: '',
            city: '',
            officeStreet: '',
            officePostalCode: '',
            officeCity: '',
            officeLatitude: null,
            officeLongitude: null,
          });
        }}
      />

      {/* After a pick, confirm the resolved address (read-only — it can only
          come from Google, never free text) and let the user adjust the two
          constrained dropdowns. Hidden until there's something to show. */}
      {draft.addressSearchText !== '' && (
        <fieldset className="form__group">
          <legend className="form__group-legend">
            {t('onboarding.orgInfo.confirm.legend')}
          </legend>
          <p className="form__hint">{t('onboarding.orgInfo.confirm.hint')}</p>

          <p className="form__readonly-value">{draft.addressSearchText}</p>

          <SearchableSelect
            labelKey="onboarding.orgInfo.country.label"
            placeholderKey="onboarding.orgInfo.country.placeholder"
            value={draft.countryCode}
            options={countryOptions}
            onChange={(code) => update({ countryCode: code })}
            required
          />

          <SearchableSelect
            labelKey="onboarding.orgInfo.language.label"
            placeholderKey="onboarding.orgInfo.language.placeholder"
            value={draft.languageCode}
            options={languageOptions}
            onChange={setLanguage}
            required
          />
        </fieldset>
      )}

      <ContactFields draft={draft} update={update} />
    </>
  );
}

// ManualMode is the degraded fallback shown only when the geocoder is
// unavailable (503 — e.g. no API key in dev). The address is typed by hand
// and carries no coordinates, so the club won't get a map pin; the normal
// flow is autocomplete-only. There is no toggle back — retrying the lookup
// wouldn't help while it's down.
function ManualMode({ draft, update, countryOptions, languageOptions, setLanguage }: SubMode) {
  const { t } = useTranslation('common');

  return (
    <>
      <p className="form__hint form__hint--warning">
        {t('onboarding.orgInfo.manualEntry.unavailableNote')}
      </p>

      <div className="form__field">
        <label htmlFor="org-city">{t('onboarding.orgInfo.city.label')}</label>
        <input
          id="org-city"
          type="text"
          value={draft.city}
          onChange={(e) => update({ city: e.target.value })}
          required
          maxLength={100}
          autoComplete="address-level2"
        />
        <p className="form__hint">{t('onboarding.orgInfo.city.hint')}</p>
      </div>

      <SearchableSelect
        labelKey="onboarding.orgInfo.country.label"
        placeholderKey="onboarding.orgInfo.country.placeholder"
        value={draft.countryCode}
        options={countryOptions}
        onChange={(code) => update({ countryCode: code })}
        required
      />

      <SearchableSelect
        labelKey="onboarding.orgInfo.language.label"
        placeholderKey="onboarding.orgInfo.language.placeholder"
        value={draft.languageCode}
        options={languageOptions}
        onChange={setLanguage}
        required
      />

      <fieldset className="form__group">
        <legend className="form__group-legend">
          {t('onboarding.orgInfo.office.legend')}
          <span className="form__group-optional">
            {t('onboarding.orgInfo.office.optional')}
          </span>
        </legend>
        <p className="form__hint">{t('onboarding.orgInfo.office.hint')}</p>

        <div className="form__field">
          <label htmlFor="org-office-street">
            {t('onboarding.orgInfo.office.street.label')}
          </label>
          <input
            id="org-office-street"
            type="text"
            value={draft.officeStreet}
            onChange={(e) => update({ officeStreet: e.target.value })}
            maxLength={200}
            autoComplete="street-address"
          />
        </div>

        <div className="form__row">
          <div className="form__field">
            <label htmlFor="org-office-postal">
              {t('onboarding.orgInfo.office.postalCode.label')}
            </label>
            <input
              id="org-office-postal"
              type="text"
              value={draft.officePostalCode}
              onChange={(e) => update({ officePostalCode: e.target.value })}
              maxLength={20}
              autoComplete="postal-code"
              inputMode="numeric"
            />
          </div>
          <div className="form__field">
            <label htmlFor="org-office-city">
              {t('onboarding.orgInfo.office.city.label')}
            </label>
            <input
              id="org-office-city"
              type="text"
              value={draft.officeCity}
              onChange={(e) => update({ officeCity: e.target.value })}
              maxLength={100}
              autoComplete="address-level2"
            />
          </div>
        </div>
      </fieldset>

      <ContactFields draft={draft} update={update} />
    </>
  );
}

// ContactFields is the optional "public contact info" block: email, phone,
// and the legal registration number (Swedish organisationsnummer / Finnish
// Y-tunnus / etc). Shown in both the autocomplete and manual modes so the
// owner can fill them during onboarding — they're equally editable later
// in Settings → Organization.
function ContactFields({
  draft,
  update,
}: {
  draft: OnboardingStepProps['draft'];
  update: OnboardingStepProps['update'];
}) {
  const { t } = useTranslation('common');

  // The org registration number is country-specific: most clubs outside the
  // Nordics have no equivalent we collect, so hide the field unless the chosen
  // country has one — and label it with the local term ("Y-tunnus", "CVR-nummer").
  const regTerm = orgNumberTerm(draft.countryCode);

  return (
    <fieldset className="form__group">
      <legend className="form__group-legend">
        {t('onboarding.orgInfo.contact.legend')}
        <span className="form__group-optional">
          {t('onboarding.orgInfo.contact.optional')}
        </span>
      </legend>
      <p className="form__hint">{t('onboarding.orgInfo.contact.hint')}</p>

      <div className="form__row">
        <div className="form__field">
          <label htmlFor="org-contact-email">
            {t('onboarding.orgInfo.contact.email.label')}
          </label>
          <input
            id="org-contact-email"
            type="email"
            value={draft.contactEmail}
            onChange={(e) => update({ contactEmail: e.target.value })}
            maxLength={200}
            autoComplete="email"
          />
        </div>
        <div className="form__field">
          <label htmlFor="org-contact-phone">
            {t('onboarding.orgInfo.contact.phone.label')}
          </label>
          <input
            id="org-contact-phone"
            type="tel"
            value={draft.contactPhone}
            onChange={(e) => update({ contactPhone: e.target.value })}
            maxLength={40}
            autoComplete="tel"
          />
        </div>
      </div>

      {regTerm && (
        <div className="form__field">
          <label htmlFor="org-registration-number">{regTerm}</label>
          <input
            id="org-registration-number"
            type="text"
            value={draft.orgRegistrationNumber}
            onChange={(e) => update({ orgRegistrationNumber: e.target.value })}
            maxLength={40}
            autoComplete="off"
          />
          <p className="form__hint">{t('onboarding.orgInfo.contact.regNumber.hint')}</p>
        </div>
      )}
    </fieldset>
  );
}

// orgInfoIsValid gates the Continue button. Beyond the identity fields, an
// address is now mandatory: either a picked place (a non-null coordinate pair
// — the normal autocomplete path) or, in the degraded manual fallback, a
// typed street. This guarantees a Go org can land on the discovery map.
export const orgInfoIsValid = (
  draft: Pick<
    OnboardingDraft,
    | 'organizationName'
    | 'city'
    | 'countryCode'
    | 'languageCode'
    | 'officeLatitude'
    | 'officeLongitude'
    | 'manualEntry'
    | 'officeStreet'
  >,
): boolean => {
  const identityOk =
    draft.organizationName.trim().length > 0 &&
    draft.city.trim().length > 0 &&
    /^[A-Z]{2}$/.test(draft.countryCode) &&
    /^[a-z]{2}$/.test(draft.languageCode);
  if (!identityOk) return false;
  const hasPin = draft.officeLatitude != null && draft.officeLongitude != null;
  const hasManualAddress = draft.manualEntry && draft.officeStreet.trim().length > 0;
  return hasPin || hasManualAddress;
};
